import { LinearProgress } from '@mui/joy';
import Alert from '@mui/joy/Alert';
import Box from '@mui/joy/Box';

import { CheckIcon } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { mutate } from 'swr';

import Breadcrumbs from '@/components/Breadcrumbs';
import EmptyState from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { SplitViewPane } from '@/components/SplitViewPane';
import useToast from '@/components/Toast/useToast';
import { PROJECTS_PAGE_TITLE } from '@/constants/pageTitlesAndDescriptionConstants';
import { FetcherParams } from '@/data/fetcher';
import { Button } from '@/design-system';
import { usePermissions } from '@/hooks/usePermissions';
import {
  useAnnotationQueue,
  useAnnotationQueueSize,
  useCreateIdentityAnnotationQueueStatusMutation,
  useDataset,
  useExample,
  useRunRules,
  useUpdateRunInAnnotationQueueMutation,
} from '@/hooks/useSwr';
import { useAlignmentStore } from '@/stores/useAlignmentStore';
import { ScrollParentProvider } from '@/utils/VirtuosoCustomScrollParentContext';
import {
  apiAnnotationQueuesPath,
  appDatasetsPath,
  appEvaluatorPath,
  appOrganizationPath,
  appProjectsPath,
  appRunPath,
  appSessionPath,
} from '@/utils/constants';
import { cn } from '@/utils/tailwind';

import { Run } from '../Run';
import { AlignAnnotationQueue } from './AlignAnnotationQueue';
import { AnnotationQueue } from './AnnotationQueue';
import { AnnotationQueueHeader } from './AnnotationQueueHeader';
import { AnnotationQueueProps } from './types';
import { useQueueRunWithPrefetch } from './utils/useQueueRunWithPrefetch';

export function AnnotationQueueEmptyState(props: {
  organizationId?: string | null;
  annotationQueueName: string;
  annotationQueueId: string;
  runRuleId?: string;
  hideHeader?: boolean;
  className?: string;
}) {
  const { data: rules } = useRunRules(
    props.runRuleId ? { id: [props.runRuleId] } : null
  );

  const setRunRuleId = useAlignmentStore((state) => state.setRunRuleId);
  const setAlignmentQueueId = useAlignmentStore(
    (state) => state.setAlignmentQueueId
  );
  const setAlignmentMode = useAlignmentStore((state) => state.setAlignmentMode);

  const aligningRule = rules?.[0];
  const evaluatorUrl = aligningRule?.dataset_id
    ? `/${appOrganizationPath}/${props.organizationId}/${appDatasetsPath}/${aligningRule?.dataset_id}/${appEvaluatorPath}/${props.runRuleId}`
    : `/${appOrganizationPath}/${props.organizationId}/${appProjectsPath}/${appSessionPath}/${aligningRule?.session_id}/${appEvaluatorPath}/${props.runRuleId}`;
  const evaluatorPlaygroundUrl = `${evaluatorUrl}/playground`;
  const addToEvaluatorUrl = aligningRule?.dataset_id
    ? `/${appOrganizationPath}/${props.organizationId}/${appDatasetsPath}/${aligningRule?.dataset_id}`
    : `/${appOrganizationPath}/${props.organizationId}/${appProjectsPath}/${appSessionPath}/${aligningRule?.session_id}`;
  const navigate = useNavigate();
  return (
    <div
      className={cn(
        '-mr-4 -mt-3 flex h-[calc(100vh_-_40px)] flex-col items-start',
        props.className
      )}
    >
      {!props.hideHeader && (
        <AnnotationQueueHeader
          organizationId={props.organizationId ?? null}
          annotationQueueName={props.annotationQueueName}
          annotationQueueId={props.annotationQueueId}
          populatingInProgress={false}
        />
      )}
      <div className="flex h-full w-full flex-col items-center justify-center gap-5">
        <EmptyState
          title="You're all caught up"
          description={
            props.runRuleId
              ? 'All sample evaluator runs have been annotated. Add more samples or refine your prompt using the Evaluator Playground.'
              : `All runs in this queue have been annotated. Add more from ${PROJECTS_PAGE_TITLE}.`
          }
          icon={<CheckIcon />}
        />
        <div className="flex gap-2">
          {props.organizationId && (
            <Button
              size="md"
              variant="outlined"
              color="secondary"
              onClick={() => {
                if (props.runRuleId) {
                  setRunRuleId(props.runRuleId);
                  setAlignmentQueueId(props.annotationQueueId);
                  setAlignmentMode(true);
                }
                navigate(
                  props.runRuleId
                    ? addToEvaluatorUrl
                    : `/${appOrganizationPath}/${props.organizationId}/projects`
                );
              }}
            >
              {props.runRuleId
                ? 'Add more sample traces'
                : `Go to ${PROJECTS_PAGE_TITLE}`}
            </Button>
          )}
          {props.organizationId && props.runRuleId && (
            <Button
              size="md"
              variant="outlined"
              color="secondary"
              onClick={() => navigate(evaluatorPlaygroundUrl)}
            >
              Go to Evaluator Playground
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

const AnnotationQueuePage = () => {
  const navigate = useNavigate();
  const { annotationQueueId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { authorize, isLoading: isPermsLoading } = usePermissions();
  const { createToast } = useToast();

  const runIndex = useMemo(
    () => +(searchParams.get('runIndex') ?? 0),
    [searchParams]
  );
  const peekedRun = searchParams.get('peekedRun');
  const [peekedRunId, setPeekedRunId] = useState(peekedRun);
  const [datasetIdState, setDatasetIDState] = useState<string | undefined>();

  const {
    data: annotationQueue,
    isLoading: isLoadingAnnotationQueue,
    mutate: mutateAnnotationQueue,
  } = useAnnotationQueue(annotationQueueId, {
    // Use the refreshInterval configuration function instead
    refreshInterval: (data) => (data?.source_rule_id ? 10000 : 0),
  });

  const {
    data: annotationQueueSize,
    mutate: mutateSize,
    isLoading: isLoadingSize,
  } = useAnnotationQueueSize(annotationQueueId);

  const {
    data: annotationQueueCurrentRun,
    isLoading: isLoadingRun,
    goToNextRun,
    error,
  } = useQueueRunWithPrefetch(
    runIndex,
    annotationQueueId,
    annotationQueueSize?.size
  );

  const hasError = !!error;
  const hasMoreToLoad = useMemo(
    () => runIndex < (annotationQueueSize?.size ?? 0) - 1,
    [annotationQueueSize?.size, runIndex]
  );

  const leftDisabled = runIndex < 1;
  const rightDisabled = !hasMoreToLoad;
  const moveToEndDisabled = rightDisabled && !hasMoreToLoad;

  const updateAnnotationQueueRuns = useUpdateRunInAnnotationQueueMutation(
    annotationQueueId,
    annotationQueueCurrentRun?.id
  );

  const logMarkAsDone = useCreateIdentityAnnotationQueueStatusMutation(
    annotationQueueId,
    annotationQueueCurrentRun?.queue_run_id,
    goToNextRun,
    true
  );
  const logMoveToEnd = useCreateIdentityAnnotationQueueStatusMutation(
    annotationQueueId,
    annotationQueueCurrentRun?.queue_run_id,
    goToNextRun,
    true
  );
  const logViewedRun = useCreateIdentityAnnotationQueueStatusMutation(
    annotationQueueId,
    annotationQueueCurrentRun?.queue_run_id
  );

  useEffect(() => {
    if (annotationQueueCurrentRun?.id) {
      logViewedRun.trigger({
        status: 'viewed',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annotationQueueCurrentRun?.id]);

  const referenceExampleResponse = useExample(
    annotationQueueCurrentRun?.reference_example_id ?? null
  );
  const referenceExample = annotationQueueCurrentRun?.reference_example_id
    ? referenceExampleResponse.data
    : undefined;

  const datasetId =
    datasetIdState ?? annotationQueue?.default_dataset ?? undefined;
  const isDefaultDataset =
    datasetId === annotationQueue?.default_dataset && !!datasetId;
  const { data: dataset } = useDataset(datasetId);

  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('peekedRun', peekedRunId ?? '');
    setSearchParams(newSearchParams, { replace: true });
  }, [peekedRunId, searchParams, setSearchParams]);

  useEffect(() => {
    if (hasError) {
      mutateSize();
    }
  }, [hasError, mutateSize]);

  useLayoutEffect(() => {
    if (
      peekedRunId &&
      annotationQueueCurrentRun?.id &&
      annotationQueueCurrentRun.id !== peekedRunId
    ) {
      setPeekedRunId(annotationQueueCurrentRun.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annotationQueueCurrentRun?.id]);

  useEffect(() => {
    if (
      runIndex !== 0 &&
      (isNaN(runIndex) || runIndex < 0 || annotationQueueSize?.size === 0)
    ) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('runIndex', '0');
      setSearchParams(newSearchParams);
    }
  }, [annotationQueueSize?.size, runIndex, searchParams, setSearchParams]);

  useEffect(() => {
    if (
      runIndex != null &&
      annotationQueueSize?.size != null &&
      runIndex > annotationQueueSize.size - 1
    ) {
      // If there are no more runs and we are past the end, then go to the last run
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set(
        'runIndex',
        Math.max(annotationQueueSize?.size - 1, 0).toString()
      );
      setSearchParams(newSearchParams);
    }
  }, [
    annotationQueueSize?.size,
    hasMoreToLoad,
    runIndex,
    searchParams,
    setSearchParams,
  ]);

  const handleMoveToEndClick = useCallback(() => {
    const now = new Date();
    if (annotationQueueCurrentRun?.id) {
      logMoveToEnd.trigger(
        {
          status: null,
          override_added_at: now
            .toISOString()
            .replace('T', ' ')
            .replace('Z', ''),
        },
        {
          onError: (e) => {
            createToast({
              title: 'Error requeueing at end',
              description: e.message,
              error: true,
            });
            mutate(
              (key: FetcherParams) =>
                key?.url ===
                  `${apiAnnotationQueuesPath}/${annotationQueueId}/size` ||
                key?.url ===
                  `${apiAnnotationQueuesPath}/${annotationQueueId}/runs`,
              null
            );
          },
        }
      );
    }
  }, [
    annotationQueueCurrentRun?.id,
    logMoveToEnd,
    createToast,
    annotationQueueId,
  ]);

  const handleMarkDone = useCallback(() => {
    if (annotationQueueCurrentRun?.id) {
      logMarkAsDone.trigger(
        { status: 'completed' },
        {
          onSuccess: () => {
            if (runIndex === (annotationQueueSize?.size ?? 0) - 1) {
              const newSearchParams = new URLSearchParams(searchParams);
              newSearchParams.set(
                'runIndex',
                Math.max(runIndex - 1, 0).toString()
              );
              setSearchParams(newSearchParams);
            }
            mutateSize(
              {
                size: annotationQueueSize?.size
                  ? annotationQueueSize.size - 1
                  : 0,
              },
              { revalidate: false }
            );
          },
          onError: (e) => {
            createToast({
              title: 'Error marking done',
              description: e.message,
              error: true,
            });
            mutate(
              (key: FetcherParams) =>
                key?.url ===
                  `${apiAnnotationQueuesPath}/${annotationQueueId}/size` ||
                key?.url ===
                  `${apiAnnotationQueuesPath}/${annotationQueueId}/runs`,
              null
            );
            mutateSize(() => undefined);
          },
        }
      );
    }
  }, [
    annotationQueueCurrentRun?.id,
    logMarkAsDone,
    runIndex,
    annotationQueueSize?.size,
    mutateSize,
    searchParams,
    setSearchParams,
    createToast,
    annotationQueueId,
  ]);

  const onFeedbackGiven = useCallback(() => {
    const now = new Date();
    updateAnnotationQueueRuns.trigger({
      json: {
        last_reviewed_time:
          now.toISOString().split('T')[0] +
          ' ' +
          now.toISOString().split('T')[1].split('.')[0],
      },
    });
  }, [updateAnnotationQueueRuns]);

  const handleNavigation = useCallback(
    (direction: 'left' | 'right') => {
      const newIndex = direction === 'left' ? runIndex - 1 : runIndex + 1;
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('runIndex', newIndex.toString());
      setSearchParams(newSearchParams);
      mutateSize();
    },
    [runIndex, searchParams, setSearchParams, mutateSize]
  );

  const handleLeftClick = useCallback(() => {
    if (!leftDisabled) handleNavigation('left');
  }, [leftDisabled, handleNavigation]);

  const handleRightClick = useCallback(() => {
    if (!rightDisabled) handleNavigation('right');
  }, [rightDisabled, handleNavigation]);

  if (isPermsLoading) {
    return <LinearProgress />;
  }

  if (!authorize('annotation-queues:read')) {
    return <ErrorBanner className="m-4">Forbidden</ErrorBanner>;
  }

  if (annotationQueue && annotationQueueId) {
    const annotationQueueProps: AnnotationQueueProps = {
      setPeekedRun: setPeekedRunId,
      index: runIndex,
      totalRuns: annotationQueueSize?.size,
      onFeedbackGiven,
      isDoneLoading: logMarkAsDone.isMutating,
      isMoveLoading: logMoveToEnd.isMutating,
      currentRun: annotationQueueCurrentRun,
      referenceExample,
      leftDisabled,
      rightDisabled,
      annotationQueue,
      handleMarkDone,
      handleMoveToEnd: handleMoveToEndClick,
      moveToEndDisabled,
      handleLeftClick,
      handleRightClick,
      dataset: dataset ?? null,
      setDatasetID: setDatasetIDState,
      isDefaultDataset,
      loading: isLoadingRun || isLoadingSize,
      mutateSize,
      mutateAnnotationQueue,
    };

    return (
      <ScrollParentProvider>
        {annotationQueue.run_rule_id ? (
          <AlignAnnotationQueue
            key={annotationQueueCurrentRun?.id}
            {...annotationQueueProps}
          />
        ) : (
          <AnnotationQueue
            key={annotationQueueCurrentRun?.id}
            {...annotationQueueProps}
          />
        )}
        <SplitViewPane
          sidePaneId="annotationQueue"
          open={!!peekedRunId}
          onClose={() => {
            setPeekedRunId(null);
          }}
          onExpand={() => {
            if (annotationQueueCurrentRun) {
              navigate(
                `${appRunPath}/${peekedRunId}?trace_id=${annotationQueueCurrentRun.trace_id}&runStartTime=${annotationQueueCurrentRun.start_time}`
              );
            }
          }}
          onNext={leftDisabled ? undefined : handleLeftClick}
          onPrevious={rightDisabled ? undefined : handleRightClick}
          className="relative"
          title={''}
        >
          <div className="absolute inset-0 flex flex-grow flex-col overflow-auto px-4">
            <Run
              runId={peekedRunId}
              traceId={annotationQueueCurrentRun?.trace_id}
              navigateToRun={(id) => {
                setPeekedRunId(id);
              }}
              runSessionId={annotationQueueCurrentRun?.session_id}
            />
          </div>
        </SplitViewPane>
      </ScrollParentProvider>
    );
  }

  if (isLoadingAnnotationQueue || isLoadingRun) {
    return <LinearProgress />;
  }

  return (
    <Box className="m-4">
      <Alert variant="outlined" color="danger">
        404: This queue does not exist
      </Alert>
    </Box>
  );
};

export default function Page() {
  return (
    <div className="px-4 pb-6 pt-3">
      <Breadcrumbs />
      <AnnotationQueuePage />
    </div>
  );
}
